import React, { useState, useRef } from 'react';
import { FilePond, registerPlugin } from 'react-filepond';
import { Button } from 'reactstrap';
import 'filepond/dist/filepond.min.css';
import { validarFacturaIA } from '../../helpers/backend_helper';
import ImageCropper from './ImageCropper';

// Plugins gratuitos
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageResize from 'filepond-plugin-image-resize';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImageCrop from 'filepond-plugin-image-crop';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

// Estilos personalizados
import './SubidaFacturaFilePond.css';

// Registrar plugins (orden importa: Preview debe ir antes de Crop)
registerPlugin(
  FilePondPluginFileValidateType,
  FilePondPluginImageValidateSize,
  FilePondPluginImagePreview,
  FilePondPluginImageCrop,
  FilePondPluginImageResize,
  FilePondPluginImageTransform
);

const SubidaFacturaFilePond = ({ onValidacionCompleta, onError, extrayendoDatos = false }) => {
  const pondRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [validando, setValidando] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [esPdf, setEsPdf] = useState(false);
  const [pdfPreview, setPdfPreview] = useState(null);

  // ... (handleAddFile se mantiene igual)

  const handleManualRemove = () => {
    if (pondRef.current) {
      pondRef.current.removeFiles();
      setFiles([]);
      setPdfPreview(null);
      setImageToCrop(null);
      setEsPdf(false);
    }
  };

  const handleManualProcess = () => {
    if (pondRef.current && files.length > 0) {
      pondRef.current.processFile();
    }
  };

  const handleAddFile = (error, file) => {
    if (error) {
      console.error('Error agregando archivo:', error);
      if (onError) onError(error);
      return;
    }
    
    // Limpiar preview anterior
    setPdfPreview(null);
    
    // Detectar si es PDF
    const isPdfFile = file && file.file && (file.file.type === 'application/pdf' || file.file.name.toLowerCase().endsWith('.pdf'));
    setEsPdf(isPdfFile);
    
    // Guardar el archivo original para el cropper
    if (file && file.file) {
      setOriginalFile(file.file);
      
      // Crear URL para el cropper (solo si NO es PDF)
      if (!isPdfFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setImageToCrop(e.target.result);
        };
        reader.readAsDataURL(file.file);
      } else {
        setImageToCrop(null); // Limpiar imagen si es PDF
      }
    }
  };
  
  const handleOpenCropper = () => {
    if (imageToCrop && !esPdf) {
      setShowCropper(true);
    }
  };
  
  const handleCropComplete = async (croppedFile) => {
    //console.log('✂️ handleCropComplete iniciado');
   /*  console.log('📁 Archivo recortado:', {
      name: croppedFile.name,
      size: croppedFile.size,
      type: croppedFile.type,
      lastModified: croppedFile.lastModified
    }); */
    
    // Validar que el archivo tenga contenido
    if (!croppedFile || croppedFile.size === 0) {
      console.error('❌ Archivo recortado vacío o inválido');
      alert('Error: La imagen recortada está vacía. Por favor, intenta nuevamente.');
      return;
    }
    
    // Validar tamaño mínimo (1KB)
    if (croppedFile.size < 1000) {
      console.error('❌ Archivo recortado muy pequeño:', croppedFile.size);
      alert('Error: La imagen recortada es muy pequeña. Por favor, selecciona un área más grande.');
      return;
    }
    
    // Reemplazar el archivo en FilePond con la imagen recortada
    setFiles([croppedFile]);
    
    // Actualizar la imagen para preview
    const reader = new FileReader();
    reader.onload = (e) => {
      //console.log('✅ Preview actualizado, tamaño base64:', e.target.result.length);
      setImageToCrop(e.target.result);
    };
    reader.onerror = (e) => {
      console.error('❌ Error leyendo archivo para preview:', e);
    };
    reader.readAsDataURL(croppedFile);
    
    // Procesar automáticamente después del recorte
    //console.log('🔄 Iniciando validación del archivo recortado...');
    setValidando(true);
    
    try {
      const formData = new FormData();
      formData.append('file', croppedFile, croppedFile.name);
      
      //console.log('📤 Enviando a /validar-imagen...');
      const resultado = await validarFacturaIA(formData);
      //console.log('✅ Resultado de validación:', resultado);
      
      setValidando(false);
      
      if (onValidacionCompleta) {
        //console.log('📢 Notificando validación completa al padre');
        onValidacionCompleta(resultado, croppedFile);
      }
    } catch (err) {
      console.error('❌ Error validando imagen recortada:', err);
      console.error('❌ Error response:', err.response);
      console.error('❌ Error data:', err.response?.data);
      
      setValidando(false);
      
      if (onError) {
        onError(err);
      } else {
        alert('Error al validar la imagen recortada. Por favor, intenta nuevamente o usa la imagen original.');
      }
    }
  };

  const handleProcessFile = async (fieldName, file, metadata, load, error, progress, abort) => {
    setValidando(true);

    try {
      // Crear FormData
      const formData = new FormData();
      formData.append('file', file, file.name);

      // Llamar al backend de validación IA usando el helper
      const resultado = await validarFacturaIA(formData);
      
      // Si hay preview (PDF o rotación), guardarlo
      if (resultado.imagen_corregida) {
        setPdfPreview(resultado.imagen_corregida);
      }
      
      setValidando(false);

      // Notificar al componente padre con resultado Y archivo original
      // IMPORTANTE: FilePond puede pasar un Blob en lugar de File, necesitamos convertirlo
      if (onValidacionCompleta) {
        // Convertir Blob a File si es necesario
        let archivoParaPasar = file;
        if (file instanceof Blob && !(file instanceof File)) {
          archivoParaPasar = new File([file], file.name || 'factura.jpg', {
            type: file.type || 'image/jpeg',
            lastModified: file.lastModified || Date.now()
          });
        }
        
        onValidacionCompleta(resultado, archivoParaPasar);
      }

      // Indicar a FilePond que el proceso terminó
      load(file.name);

    } catch (err) {
      console.error('❌ Error validando imagen:', err);
      setValidando(false);
      
      if (onError) onError(err);
      error('Error validando la imagen');
    }

    // Retornar función abort
    return {
      abort: () => {
        abort();
      }
    };
  };

  return (
    <div className="subida-factura-filepond">
      <FilePond
        ref={pondRef}
        files={files}
        onupdatefiles={setFiles}
        onaddfile={handleAddFile}
        allowMultiple={false}
        maxFiles={1}
        name="factura"
        labelIdle='<span class="filepond--label-action">Arrastra tu factura aquí o haz clic para buscar</span>'
        
        // Validación de tipo
        acceptedFileTypes={['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']}
        fileValidateTypeLabelExpectedTypes="Se permiten imágenes JPG, PNG o archivos PDF"
        labelFileTypeNotAllowed="Tipo de archivo no válido"
        
        // Validación de tamaño
        allowImageValidateSize={true}
        imageValidateSizeMinWidth={400}
        imageValidateSizeMinHeight={400}
        imageValidateSizeLabelImageSizeTooSmall="La imagen es muy pequeña. Mínimo 400x400 píxeles"
        imageValidateSizeLabelImageSizeTooBig="La imagen es muy grande. Máximo 4000x4000 píxeles"
        imageValidateSizeMaxWidth={4000}
        imageValidateSizeMaxHeight={4000}
        
        // Resize automático (optimización)
        allowImageResize={true}
        imageResizeTargetWidth={1200}
        imageResizeTargetHeight={1600}
        imageResizeMode="contain"
        imageResizeUpscale={false}
        
        // Transform (aplicar cambios antes de subir)
        allowImageTransform={true}
        imageTransformOutputQuality={95}
        imageTransformOutputMimeType="image/jpeg"
        imageTransformOutputStripImageHead={false}
        
        // Preview
        allowImagePreview={true}
        imagePreviewHeight={300}
        imagePreviewMaxHeight={400}
        imagePreviewTransparencyIndicator="grid"
        
        // Crop (recorte manual por el usuario con guías rojas)
        allowImageCrop={true}
        imageCropAspectRatio="free"
        
        // IMPORTANTE: Deshabilitar procesamiento automático para permitir edición
        allowProcess={true}
        
        // Textos del botón de crop
        labelButtonEditItem="✂️ Recortar Imagen"
        
        // Server (procesamiento custom)
        server={{
          process: handleProcessFile
        }}
        
        // Textos en español
        labelFileProcessing="Validando"
        labelFileProcessingComplete="Validación completa"
        labelFileProcessingAborted="Validación cancelada"
        labelFileProcessingError="Error en validación"
        labelTapToCancel="toca para cancelar"
        labelTapToRetry="toca para reintentar"
        labelTapToUndo="toca para deshacer"
        labelButtonRemoveItem="Eliminar"
        labelButtonAbortItemLoad="Cancelar"
        labelButtonRetryItemLoad="Reintentar"
        labelButtonAbortItemProcessing="Cancelar"
        labelButtonUndoItemProcessing="Deshacer"
        labelButtonRetryItemProcessing="Reintentar"
        labelButtonProcessItem="Validar"
        
        // Estilos
        stylePanelLayout="compact"
        styleLoadIndicatorPosition="center bottom"
        styleProgressIndicatorPosition="right bottom"
        styleButtonRemoveItemPosition="left bottom"
        styleButtonProcessItemPosition="right bottom"
        styleButtonEditItemPosition="center bottom"
        
        // Configuración adicional
        instantUpload={false}
        allowRevert={false}
        credits={false}
      />

      {/* --- ÁREA DE ACCIONES Y ESTADO --- */}

      {/* Botones de Acción Unificados */}
      {!validando && !extrayendoDatos && files.length > 0 && (
        <div className="action-buttons-container">
          {/* 1. Eliminar */}
          <button 
            className="btn-modern btn-cancel" 
            onClick={handleManualRemove}
            title="Eliminar archivo"
          >
            <i className="ri-delete-bin-line"></i>
            Eliminar
          </button>

          {/* 2. Recortar (Solo imágenes) */}
          {imageToCrop && !esPdf && (
            <button 
              className="btn-modern btn-crop" 
              onClick={handleOpenCropper}
              title="Recortar imagen manualmente"
            >
              <i className="ri-crop-line"></i>
              Recortar
            </button>
          )}

          {/* 3. Procesar/Validar */}
          <button 
            className="btn-modern btn-process" 
            onClick={handleManualProcess}
            title="Enviar a validar con IA"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white' }}
          >
            <i className="ri-check-double-line"></i>
            Procesar
          </button>
        </div>
      )}
      
      {/* 2. Estado: PDF Detectado */}
      {esPdf && files.length > 0 && !validando && !extrayendoDatos && (
        <div className="status-card state-pdf">
          <div className="status-icon-wrapper">
            <i className="ri-file-pdf-2-line"></i>
          </div>
          <h5 className="status-title">Documento PDF Detectado</h5>
          <p className="status-desc">Se procesará la primera página del documento.</p>
          
          {pdfPreview && (
            <div className="preview-container">
              <img 
                src={pdfPreview} 
                alt="Vista previa PDF" 
                className="preview-image"
                style={{ maxHeight: '250px', maxWidth: '100%' }}
              />
            </div>
          )}
        </div>
      )}

      {/* 3. Estado: Validando Imagen */}
      {validando && (
        <div className="status-card state-validating">
          <div className="status-icon-wrapper">
            <i className="ri-search-eye-line"></i>
          </div>
          <h5 className="status-title">Analizando Imagen...</h5>
          <p className="status-desc">Verificando calidad, nitidez y legibilidad.</p>
        </div>
      )}

      {/* 4. Estado: Extrayendo Datos (IA) */}
      {extrayendoDatos && (
        <div className="status-card state-extracting">
          <div className="status-icon-wrapper">
            <i className="ri-brain-line"></i>
          </div>
          <h5 className="status-title">Inteligencia Artificial Trabajando...</h5>
          <p className="status-desc">Extrayendo medicamentos, precios y cantidades.</p>
        </div>
      )}

      {/* Modal de cropper con guías rojas */}
      <ImageCropper
        image={imageToCrop}
        isOpen={showCropper}
        toggle={() => setShowCropper(false)}
        onCropComplete={handleCropComplete}
      />
    </div>
  );
};

export default SubidaFacturaFilePond;
